import {
  applyOperation,
  formatEquation,
  normalizeOperation,
  substitute,
  type Equation,
  type AlgebraOperation,
} from './algebra.ts';

/** Algebra is validated before turning it into reusable, cacheable narration. */
export const speakMath = (text: string) =>
  text
    .replace(/(\d+(?:\/\d+)?)x/g, '$1 times x')
    .replace(/=/g, ' equals ')
    .replace(/\+/g, ' plus ')
    .replace(/[-−]/g, ' minus ')
    .replace(/\//g, ' over ')
    .replace(/\s+/g, ' ')
    .trim();
export function operationNarration(
  before: Equation,
  input: AlgebraOperation,
): string {
  const operation = normalizeOperation(input);
  const after = applyOperation(before, operation);
  const value = speakMath(operation.value);
  if(operation.kind==='divide') {
    // Keep the Divide / Factor / Now and below / bottom cues in order: both
    // representations use these aligned words to reveal the same mathematics.
    return `Start with ${speakMath(formatEquation(before))}. Divide both entire sides by ${value} to keep equal values equal. Write each entire expression above a fraction bar, with ${value} below. Factor each numerator; cancel the common factor ${value} on top and bottom. ${value} over ${value} equals one. Now we have ${speakMath(formatEquation(after))}. Multiplying both sides by ${value} reverses this step, so the solutions stay the same. Why must we divide both sides?`;
  }
  const move =
    operation.kind === 'subtract'
      ? `Subtract ${value} from both entire sides`
      : operation.kind === 'add'
        ? `Add ${value} to both entire sides`
        : `Multiply both entire sides by ${value}`;
  return `Start with ${speakMath(formatEquation(before))}. ${move}. We make the same change to equal values, so they remain equal. Now we have ${speakMath(formatEquation(after))}. We can undo this move on both sides, so the solutions stay the same. Why must we change both sides?`;
}

/** A deeper reason beside the original equation, without narrating unseen steps. */
export function operationReasonNarration(before: Equation, input: AlgebraOperation): string {
  const operation = normalizeOperation(input);
  applyOperation(before, operation); // Share the same admissibility checks as a move.
  const value = speakMath(operation.value);
  const reason = operation.kind === 'divide'
    ? `Dividing both equal values by the same nonzero number keeps them equal. Multiplying by ${value} undoes division by ${value}. Zero is excluded because division by zero is undefined.`
    : operation.kind === 'multiply'
      ? `Multiplying both equal values by the same nonzero number keeps them equal. Dividing by ${value} undoes multiplication by ${value}. Multiplying by zero would erase information, so it cannot preserve the solutions.`
      : operation.kind === 'add'
        ? `Adding the same quantity to equal values keeps them equal. Subtracting ${value} undoes adding ${value}, for every possible value of x.`
        : `Subtracting the same quantity from equal values keeps them equal. Adding ${value} undoes subtracting ${value}, for every possible value of x.`;
  return `Look at ${speakMath(formatEquation(before))}. The equals sign says that both sides have equal values. ${reason} Because the change is reversible, exactly the same values of x satisfy the equation. What could go wrong if we changed only one side?`;
}
export function candidateNarration(original: Equation, value: string): string {
  const check = substitute(original, value);
  return `${check.equal ? 'Yes, that value checks.' : 'Let us check that value together.'} Start with ${speakMath(formatEquation(original))}. Replace every x with ${speakMath(value)}. Multiply and add on each side. The left side is ${speakMath(check.left)}. The right side is ${speakMath(check.right)}. ${check.equal ? 'Both sides are equal, so this value is a solution. How did you check your value in the original equation?' : 'The sides are not equal, so this value does not solve the original equation. Which step in your work would you check next?'}`;
}

const small: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  fifteen: 15,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  ninety: 90,
};
export function thinkingTimeRequest(text: string): number | undefined {
  const clean = text
    .toLowerCase()
    .trim()
    .replace(/[.!?]+$/, '');
  if (
    /^(?:please )?(?:i need|give me|let me have)(?: a little| some)? more time(?: to think)?(?: please)?$/.test(
      clean,
    )
  )
    return 120000;
  const match = clean.match(
    /^(?:please )?(?:give me|i need|wait|check (?:back|in) (?:in|after)|leave me for) (\d+|one|two|three|four|five|six|seven|eight|nine|ten|fifteen|twenty|thirty|forty|fifty|sixty|ninety) (seconds?|minutes?)(?: to think)?(?: please)?$/,
  );
  if (!match) return;
  const n = small[match[1]] ?? Number(match[1]);
  if (!Number.isFinite(n) || n <= 0) return;
  return Math.min(3600000, n * (match[2].startsWith('minute') ? 60000 : 1000));
}
export function shouldOfferHelp(s: {
  now: number;
  lastActivity: number;
  snoozedUntil: number;
  interval: number;
  enabled: boolean;
  available: boolean;
  occupied: boolean;
  visible: boolean;
  offered: boolean;
}) {
  return (
    s.enabled &&
    s.available &&
    !s.occupied &&
    s.visible &&
    !s.offered &&
    s.now >= s.snoozedUntil &&
    s.now - s.lastActivity >= s.interval
  );
}
